import { UseFormReturnType } from '@mantine/form';
import { create, StateCreator } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import type {
  CreateClaimForm,
  EditClaimForm,
  IndividualBulkClaimForm,
  SharedClaimForm,
} from 'interfaces';
import { nowISO } from 'utils/dateUtils';
import { isRecord } from 'utils/objectUtils';

export type CreateDraft = {
  type: 'create';
  rowId: string;
  data: CreateClaimForm;
  savedAt: string;
};

export type EditDraft = {
  type: 'edit';
  rowId: string;
  data: EditClaimForm;
  savedAt: string;
};

export type BulkDraft = {
  type: 'bulk';
  shared: SharedClaimForm;
  data: IndividualBulkClaimForm[];
  savedAt: string;
};

type ClaimDraftPersistedState = {
  create?: CreateDraft;
  edit?: Record<string, EditDraft>;
  bulk?: BulkDraft;
};

export type ClaimDraftPersistedPayload = {
  state: ClaimDraftPersistedState;
  version: number;
};

type ClaimDraftState = ClaimDraftPersistedState & {
  getEditDraft: () => EditClaimForm | undefined;
  listEditDraftIds: () => string[];

  saveCreateDraft: (
    rowId: string,
    form: UseFormReturnType<CreateClaimForm>
  ) => void;

  saveEditDraft: (
    rowId: string,
    form: UseFormReturnType<EditClaimForm>
  ) => void;
  getEditDraftById: (rowId: string) => EditClaimForm | undefined;
  saveBulkDraftFromRows: (
    shared: SharedClaimForm,
    rows: IndividualBulkClaimForm[]
  ) => void;
  getCreateDraft: () => CreateClaimForm | undefined;
  getBulkDraft: () => IndividualBulkClaimForm[] | undefined;
  getBulkDraftFull: () => BulkDraft | undefined;
  clearAllDrafts: () => void;
  clearCreateDraft: () => void;
  clearEditDraft: (rowId: string) => void;
  clearAllEditDrafts: () => void;
  clearBulkDraft: () => void;
};

export const CLAIM_DRAFT_STORAGE_KEY = 'claim-drafts';

const isCreateDraft = (value: unknown): value is CreateDraft =>
  isRecord(value) &&
  value.type === 'create' &&
  typeof value.rowId === 'string' &&
  typeof value.savedAt === 'string' &&
  isRecord(value.data);

const isEditDraft = (value: unknown): value is EditDraft =>
  isRecord(value) &&
  value.type === 'edit' &&
  typeof value.rowId === 'string' &&
  typeof value.savedAt === 'string' &&
  isRecord(value.data);

const isBulkDraft = (value: unknown): value is BulkDraft =>
  isRecord(value) &&
  value.type === 'bulk' &&
  typeof value.savedAt === 'string' &&
  isRecord(value.shared) &&
  Array.isArray(value.data);

const isEditDraftRecord = (
  value: unknown
): value is Record<string, EditDraft> =>
  isRecord(value) &&
  Object.entries(value).every(
    ([key, draft]) => typeof key === 'string' && isEditDraft(draft)
  );

const isClaimDraftPersistedState = (
  value: unknown
): value is ClaimDraftPersistedState =>
  isRecord(value) &&
  Object.keys(value).every((key) => ['create', 'edit', 'bulk'].includes(key)) &&
  (value.create === undefined || isCreateDraft(value.create)) &&
  (value.edit === undefined || isEditDraftRecord(value.edit)) &&
  (value.bulk === undefined || isBulkDraft(value.bulk));

export const isClaimDraftPersistedPayload = (
  value: unknown
): value is ClaimDraftPersistedPayload =>
  isRecord(value) &&
  typeof value.version === 'number' &&
  isClaimDraftPersistedState(value.state);

export const exportClaimDraftBackup = (): ClaimDraftPersistedPayload | null => {
  const raw = localStorage.getItem(CLAIM_DRAFT_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return isClaimDraftPersistedPayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const getPersistedBulkDraft = (): BulkDraft | undefined =>
  exportClaimDraftBackup()?.state.bulk;

export const importClaimDraftBackup = (payload: unknown): void => {
  if (!isClaimDraftPersistedPayload(payload)) {
    throw new Error('Invalid claim draft backup payload');
  }

  const nextState: ClaimDraftPersistedState = {
    create: payload.state.create,
    edit: payload.state.edit,
    bulk: payload.state.bulk,
  };

  useClaimDraftStore.setState(nextState);
  localStorage.setItem(CLAIM_DRAFT_STORAGE_KEY, JSON.stringify(payload));
};

const claimDraftStore: StateCreator<
  ClaimDraftState,
  [['zustand/devtools', never]],
  [['zustand/persist', unknown]],
  ClaimDraftState
> = (set, get) => ({
  // --- helpers -----------------------------------------------------------
  // Lightweight deep clone to snapshot form state (avoids later mutation
  // side-effects when Mantine form mutates nested objects in place).
  // Using JSON here since form values are plain data (dates already strings).
  _clone<T>(obj: T): T {
    return obj == null ? obj : JSON.parse(JSON.stringify(obj));
  },

  clearAllDrafts: () =>
    set({ create: undefined, edit: undefined, bulk: undefined }),

  clearCreateDraft: () => set({ create: undefined }),
  clearEditDraft: (rowId) =>
    set((state) => {
      if (!state.edit) return state;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [rowId]: data, ...rest } = state.edit;
      return { edit: Object.keys(rest).length ? rest : undefined };
    }),
  clearAllEditDrafts: () => set({ edit: undefined }),
  clearBulkDraft: () => set({ bulk: undefined }),

  saveCreateDraft: (rowId, form) => {
    const clone = (get() as any)._clone;
    set({
      create: {
        rowId,
        data: clone(form.getValues()),
        savedAt: nowISO(),
        type: 'create',
      },
    });
  },
  saveEditDraft(rowId, form) {
    const clone = (get() as any)._clone;
    const draft: EditDraft = {
      rowId,
      data: clone(form.getValues()),
      savedAt: nowISO(),
      type: 'edit',
    };
    set((state) => ({
      edit: { ...(state.edit || {}), [rowId]: draft },
    }));
  },
  saveBulkDraftFromRows(shared, rows) {
    const clone = (get() as any)._clone;
    set({
      bulk: {
        shared: clone(shared),
        data: clone(rows),
        savedAt: nowISO(),
        type: 'bulk',
      },
    });
  },

  getCreateDraft: () => get().create?.data,
  getEditDraft: () => {
    const edits = get().edit;
    if (!edits) return undefined;
    const latest = Object.values(edits).sort((a, b) =>
      b.savedAt.localeCompare(a.savedAt)
    )[0];
    return latest?.data;
  },
  getEditDraftById: (rowId: string) => get().edit?.[rowId]?.data,
  listEditDraftIds: () =>
    get().edit ? Object.keys(get().edit as Record<string, EditDraft>) : [],
  getBulkDraft: () => get().bulk?.data,
  getBulkDraftFull: () => get().bulk,
});

export const useClaimDraftStore = create<ClaimDraftState>()(
  devtools(
    persist(claimDraftStore, {
      name: CLAIM_DRAFT_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    }),
    {
      name: 'ClaimDraftStore',
      store: 'ClaimDraftStore',
      enabled: true,
    }
  )
);
